import { useEffect, useRef, useState } from 'react';

const easeOutCubic = (t) => 1 - (1 - t) ** 3;

export function useCountUp(target, { duration = 1500, delay = 0, threshold = 0.35 } = {}) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    let frameId;
    let timeoutId;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        observer.disconnect();

        timeoutId = window.setTimeout(() => {
          const start = performance.now();

          const tick = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            const next = Math.round(easeOutCubic(progress) * target);
            setValue(next);

            if (progress < 1) {
              frameId = requestAnimationFrame(tick);
            } else {
              setValue(target);
              setIsComplete(true);
            }
          };

          frameId = requestAnimationFrame(tick);
        }, delay);
      },
      { threshold },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      window.clearTimeout(timeoutId);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [target, duration, delay, threshold]);

  return { ref, value, isComplete };
}
